//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';
import { createSelector } from 'reselect';

import { ICuralateStore } from '@models/AppState';
import DateString from '@models/DateString';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import {
  tiktokEngagementMetricNames,
  TikTokEngagementPerformanceOption,
} from '@tiktok/components/analytics/engagement/tiktokEngagementConstants';

const getTikTokEngagement = (state: ICuralateStore) => state.tiktok.engagement;

export const getTikTokEngagementSummaryStats = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.summaryStats,
);

export const getTikTokEngagementDateRange = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.dateRange,
);

export const getTikTokEngagementIsFetchingTopMedia = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.flags.isFetchingTopMedia,
);

export const getTikTokEngagementIsFetchingEngagementStats = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.flags.isFetchingEngagementStats,
);

export const getTikTokEngagementMediaPerformanceIsOrderAscending =
  createSelector(
    getTikTokEngagement,
    (tiktokEngagement) => tiktokEngagement.mediaPerformance.isOrderAscending,
  );

export const getTikTokEngagementMediaPerformanceLimit = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.limit,
);

export const getTikTokEngagementMediaPerformanceMedia = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.media,
);

export const getTikTokEngagementMediaPerformanceOffset = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.offset,
);

export const getTikTokEngagementMediaPerformanceSortKey = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.sortKey,
);

export const getTikTokEngagementMediaPerformanceTotal = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.total,
);

export const getTikTokEngagementMediaPerformanceView = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.mediaPerformance.view,
);

export const getTikTokEngagementDailyStats = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.dailyStats,
);

export const getTikTokEngagementCurrentGraphMetric = createSelector(
  getTikTokEngagement,
  (tiktokEngagement) => tiktokEngagement.currentGraphMetric,
);

interface IParsedMetric {
  categories: string[];
  label: TikTokEngagementPerformanceOption;
  series: number[];
}

/**
 * This returns formatted daily stats for a specific metric (`dataType`) needed for daily performance charts
 *
 */
export const getTikTokEngagementDailyStatsForChartData = createSelector(
  [
    getTikTokEngagementDailyStats,
    (_state: ICuralateStore, dataType: TikTokEngagementPerformanceOption) =>
      dataType,
  ],
  (dailyStats, dataType) => {
    const metricName = tiktokEngagementMetricNames[dataType];

    return dailyStats.reduce(
      (data: IParsedMetric, stat) => {
        data.categories.push(DateString.fromString(stat.date, 'YYYY MM DD'));
        data.series.push(stat.metrics[metricName] ?? 0); // using 0 because non-numeric items appear as a break in the chart

        return data;
      },
      { categories: [], label: dataType, series: [] },
    );
  },
);

export const getShouldShowDelayedMetricsBanner = createSelector(
  getTikTokEngagementDateRange,
  getUserTimeZone,
  (dateRange, timeZone) => {
    const startOfToday = moment.tz(timeZone).startOf('day');
    const startOfEndDate = moment
      .tz(dateRange.endDate.toString(), timeZone)
      .startOf('day');

    return (
      Math.round(moment.duration(startOfToday.diff(startOfEndDate)).asDays()) <=
      2
    );
  },
);
